import React, { useMemo, useState, useEffect } from "react";
import Footer from "@shared/components/Footer";
import { apiUrl } from "@shared/utils/api";
import defaultCustomerLogo from "@shared/assets/odp.png";
import Select from "@shared/components/Select";
import LogoOneDoor from "@shared/components/LogoOneDoor";
import { toCanonicalEnvironmentTypeSlug } from "@shared/utils/customerEnvironmentType";

const BROKER_PROVIDER_OPTIONS = [
  { value: "cognito", label: "Cognito" },
  { value: "oidc", label: "OIDC" },
  { value: "saml", label: "SAML" },
];

const BROKER_PROVIDER_HINTS = {
  cognito: "ODP will auto-configure Cognito for the selected stage. Provide display name and email domains only.",
  oidc: "Any OIDC IdP (Okta, Entra, etc.): issuer and OAuth app credentials. After provisioning, register Cognito’s /oauth2/idpresponse URL in that IdP app.",
  saml:
    "SAML 2.0 IdP: metadata or manual fields as prompted. After provisioning, configure Reply URL (ACS) to Cognito’s /saml2/idpresponse on the hosted UI domain (or SP metadata) — not the portal /auth/callback URL.",
};

function normalizeOnboardingBrokerProvider(raw) {
  const s = String(raw || "").trim().toLowerCase();
  if (s === "okta" || s === "azure_ad") return "oidc";
  return s;
}

const REVIEW_SECTION_FIELD_MAP = {
  identity_provider: ["broker_provider", "idp_display_name", "local_cognito_enabled"],
  email_domains: ["email_domains_text"],
  saml_metadata: ["metadata_url", "metadata_xml", "entity_id", "sso_url"],
  oidc_endpoints: ["issuer", "authorization_endpoint", "token_endpoint", "jwks_uri"],
  client_credentials: ["client_id", "client_secret", "authorize_scopes"],
  environment_selection: ["environment"],
  other: [],
};

const REVIEW_FIELD_ALIASES = {
  email_domains: "email_domains_text",
};

const VALID_ONBOARDING_SLUGS = new Set(["development", "qa", "uat", "production"]);

const INITIAL_FORM = {
  broker_provider: "oidc",
  environment: "uat",
  idp_display_name: "",
  email_domains_text: "",
  local_cognito_enabled: "",
  metadata_url: "",
  metadata_xml: "",
  entity_id: "",
  sso_url: "",
  issuer: "",
  client_id: "",
  client_secret: "",
  authorize_scopes: "openid email profile",
  authorization_endpoint: "",
  token_endpoint: "",
  jwks_uri: "",
};

const EXPIRED_INVITE_TITLE = "Invite Link Expired";
const EXPIRED_INVITE_MESSAGE = "This invite link has expired. Please contact your customer success manager.";

function asFormText(value) {
  if (value == null) return "";
  return String(value);
}

function asFormBooleanChoice(value) {
  if (value === true) return "true";
  if (value === false) return "false";
  if (value == null) return "";
  const normalized = String(value).trim().toLowerCase();
  if (["true", "1", "yes", "y"].includes(normalized)) return "true";
  if (["false", "0", "no", "n"].includes(normalized)) return "false";
  return "";
}

function buildFormStateFromPrefill(prefill) {
  if (!prefill || typeof prefill !== "object") return { ...INITIAL_FORM };
  const brokerProvider = normalizeOnboardingBrokerProvider(prefill.broker_provider);
  const resolvedBrokerProvider = BROKER_PROVIDER_OPTIONS.some((option) => option.value === brokerProvider)
    ? brokerProvider
    : INITIAL_FORM.broker_provider;
  const fromPrefill = toCanonicalEnvironmentTypeSlug(prefill?.environment) || String(prefill?.environment || "").trim().toLowerCase();
  return {
    ...INITIAL_FORM,
    broker_provider: resolvedBrokerProvider,
    environment: fromPrefill && VALID_ONBOARDING_SLUGS.has(fromPrefill) ? fromPrefill : INITIAL_FORM.environment,
    idp_display_name: asFormText(prefill.idp_display_name),
    email_domains_text: asFormText(prefill.email_domains_text),
    local_cognito_enabled: resolvedBrokerProvider === "cognito" ? "true" : asFormBooleanChoice(prefill.local_cognito_enabled),
    metadata_url: asFormText(prefill.metadata_url),
    metadata_xml: asFormText(prefill.metadata_xml),
    entity_id: asFormText(prefill.entity_id),
    sso_url: asFormText(prefill.sso_url),
    issuer: asFormText(prefill.issuer),
    client_id: asFormText(prefill.client_id),
    client_secret: asFormText(prefill.client_secret),
    authorize_scopes: asFormText(prefill.authorize_scopes) || INITIAL_FORM.authorize_scopes,
    authorization_endpoint: asFormText(prefill.authorization_endpoint),
    token_endpoint: asFormText(prefill.token_endpoint),
    jwks_uri: asFormText(prefill.jwks_uri),
  };
}

function normalizeReviewField(value) {
  const normalized = String(value || "").trim().toLowerCase().replace(/[^a-z0-9_]+/g, "_");
  return REVIEW_FIELD_ALIASES[normalized] || normalized;
}

function normalizeEmailDomains(rawText) {
  return (rawText || "")
    .split(/[\n,]/)
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean)
    .map((domain) => (domain.startsWith("@") ? domain : `@${domain}`));
}

function getProtocolForProvider(brokerProvider) {
  if (brokerProvider === "cognito") return "cognito";
  return brokerProvider === "saml" ? "saml" : "oidc";
}

function isValidHttpUrl(value) {
  if (typeof value !== "string" || !value.trim()) return false;
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function validatePayload(payload) {
  const errors = {};

  if (!payload.idp_display_name?.trim()) errors.idp_display_name = "Required";
  if (!VALID_ONBOARDING_SLUGS.has(
    toCanonicalEnvironmentTypeSlug(payload.environment) || String(payload.environment || "").trim().toLowerCase()
  )) {
    errors.environment = "Must be Development, QA, Staging (UAT), or Production";
  }
  if (!payload.broker_provider) {
    errors.broker_provider = "Select an identity type";
  }
  if (!Array.isArray(payload.email_domains) || payload.email_domains.length === 0) {
    errors.email_domains = "At least one email domain is required";
  }
  if (typeof payload.local_cognito_enabled !== "boolean") {
    errors.local_cognito_enabled = "Select whether local Cognito users should be enabled";
  }

  const protocol = getProtocolForProvider(payload.broker_provider);
  if (protocol === "saml") {
    if (!payload.metadata_url?.trim() && !payload.metadata_xml?.trim()) {
      errors.metadata_url = "Metadata URL or XML required";
    }
    if (payload.metadata_url?.trim() && !isValidHttpUrl(payload.metadata_url)) {
      errors.metadata_url = "Metadata URL must be a valid URL";
    }
    if (payload.sso_url?.trim() && !isValidHttpUrl(payload.sso_url)) {
      errors.sso_url = "SSO URL must be a valid URL";
    }
  } else if (protocol === "oidc") {
    if (!payload.issuer?.trim()) {
      errors.issuer = "Issuer URL is required";
    } else if (!isValidHttpUrl(payload.issuer)) {
      errors.issuer = "Issuer URL must be a valid URL";
    }
    if (!payload.client_id?.trim()) errors.client_id = "Client ID is required";
    if (!payload.client_secret?.trim()) errors.client_secret = "Client secret is required";

    if (payload.authorization_endpoint?.trim() && !isValidHttpUrl(payload.authorization_endpoint)) {
      errors.authorization_endpoint = "Authorization endpoint must be a valid URL";
    }
    if (payload.token_endpoint?.trim() && !isValidHttpUrl(payload.token_endpoint)) {
      errors.token_endpoint = "Token endpoint must be a valid URL";
    }
    if (payload.jwks_uri?.trim() && !isValidHttpUrl(payload.jwks_uri)) {
      errors.jwks_uri = "JWKS URI must be a valid URL";
    }
  }

  return errors;
}

export default function OnboardingProvisioningPage({ brand }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [authenticating, setAuthenticating] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [initialForm, setInitialForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [requiresLogin, setRequiresLogin] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [initialLoginForm, setInitialLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [status, setStatus] = useState({ type: "", title: "", message: "" });

  const token = useMemo(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("token")?.trim() || "";
  }, []);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setStatus({
          type: "error",
          title: EXPIRED_INVITE_TITLE,
          message: EXPIRED_INVITE_MESSAGE,
        });
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(apiUrl(`/api/v1/provisioning/validate?token=${encodeURIComponent(token)}`));
        const data = await response.json().catch(() => ({}));
          if (response.ok && data.customer_id) {
            const needsLogin = Boolean(data.requires_login);
            setRequiresLogin(needsLogin);
          setLoginError("");
          setCustomer(data);
          setErrors({});
          setStatus({ type: "", title: "", message: "" });
            if (needsLogin) {
              const nextLoginForm = {
                email: data.login_email || "",
                password: "",
              };
              setLoginForm(nextLoginForm);
              setInitialLoginForm(nextLoginForm);
            } else {
              const nextForm = buildFormStateFromPrefill(data.prefill);
              setForm(nextForm);
              setInitialForm(nextForm);
            }
          } else {
          setStatus({
            type: "error",
            title: EXPIRED_INVITE_TITLE,
            message: EXPIRED_INVITE_MESSAGE,
          });
        }
      } catch {
        setStatus({
          type: "error",
          title: "Validation Failed",
          message: "Could not validate this onboarding link. Please try again.",
        });
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const logoSrc = brand?.logoSrc || defaultCustomerLogo;
  const logoAlt = brand?.alt || "One Door Platform";
  const environmentOptions = [
    { value: "development", label: "Development" },
    { value: "qa", label: "QA" },
    { value: "uat", label: "Staging (UAT)" },
    { value: "production", label: "Production" },
  ];

  const clearFieldErrors = (fieldName) => {
    setErrors((prev) => {
      const next = { ...prev };
      let changed = false;

      if (Object.prototype.hasOwnProperty.call(next, fieldName)) {
        delete next[fieldName];
        changed = true;
      }
      if (fieldName === "email_domains_text" && Object.prototype.hasOwnProperty.call(next, "email_domains")) {
        delete next.email_domains;
        changed = true;
      }

      return changed ? next : prev;
    });
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    clearFieldErrors(name);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrors({});

    const protocol = getProtocolForProvider(form.broker_provider);
    const localCognitoEnabled =
      form.broker_provider === "cognito"
        ? true
        : form.local_cognito_enabled === "true"
        ? true
        : form.local_cognito_enabled === "false"
          ? false
          : null;
    const payload = {
      token,
      auth_type: protocol,
      broker_provider: form.broker_provider,
      environment: form.environment,
      idp_display_name: form.idp_display_name.trim(),
      email_domains: normalizeEmailDomains(form.email_domains_text),
      local_cognito_enabled: localCognitoEnabled,
    };

    if (protocol === "saml") {
      payload.metadata_url = form.metadata_url.trim();
      payload.metadata_xml = form.metadata_xml.trim();
      payload.entity_id = form.entity_id.trim();
      payload.sso_url = form.sso_url.trim();
    } else if (protocol === "oidc") {
      payload.issuer = form.issuer.trim();
      payload.client_id = form.client_id.trim();
      payload.client_secret = form.client_secret.trim();
      payload.authorize_scopes = form.authorize_scopes.trim() || "openid email profile";
      payload.authorization_endpoint = form.authorization_endpoint.trim();
      payload.token_endpoint = form.token_endpoint.trim();
      payload.jwks_uri = form.jwks_uri.trim();
    }

    const clientErrors = validatePayload(payload);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(apiUrl("/api/v1/provisioning/submit"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setStatus({
          type: "success",
          title: "Submitted for Review",
          message: data.message || "Submitted for review. An administrator will provision your identity configuration.",
        });
        setCustomer(null);
        return;
      }

      if (response.status === 400 && data.fields) {
        setErrors(data.fields);
        return;
      }

      setStatus({
        type: "error",
        title: "Submission Failed",
        message: data.error || "Unable to submit onboarding details.",
      });
    } catch {
      setStatus({
        type: "error",
        title: "Network Error",
        message: "Unable to submit onboarding details. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    if (!token) return;
    setLoginError("");

    const email = String(loginForm.email || "").trim();
    const password = String(loginForm.password || "");
    if (!email || !password) {
      setLoginError("Email and temporary password are required.");
      return;
    }

    try {
      setAuthenticating(true);
      const response = await fetch(apiUrl("/api/v1/provisioning/login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, email, password }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setLoginError(data.error || "Unable to validate credentials.");
        return;
      }

      setRequiresLogin(false);
      setCustomer(data);
      const nextForm = buildFormStateFromPrefill(data.prefill);
      setForm(nextForm);
      setInitialForm(nextForm);
      setErrors({});
      setLoginForm((prev) => ({ ...prev, password: "" }));
    } catch {
      setLoginError("Unable to validate credentials. Please try again.");
    } finally {
      setAuthenticating(false);
    }
  };

  const oidcHint = BROKER_PROVIDER_HINTS[form.broker_provider] || "";
  const isSamlProvider = getProtocolForProvider(form.broker_provider) === "saml";
  const isOidcProvider = getProtocolForProvider(form.broker_provider) === "oidc";
  const isManagedCognitoProvider = getProtocolForProvider(form.broker_provider) === "cognito";
  const isLoginDirty = loginForm.email !== initialLoginForm.email || loginForm.password !== initialLoginForm.password;
  const isProvisioningFormDirty = useMemo(
    () =>
      Object.keys(INITIAL_FORM).some((key) => {
        const left = form[key] == null ? "" : String(form[key]);
        const right = initialForm[key] == null ? "" : String(initialForm[key]);
        return left !== right;
      }),
    [form, initialForm]
  );
  const reviewComments = useMemo(() => (
    Array.isArray(customer?.onboarding_review_comments) ? customer.onboarding_review_comments : []
  ), [customer?.onboarding_review_comments]);
  const showRequestedChanges = customer?.onboarding_review_status === "changes_requested";
  const { fieldComments: reviewCommentsByField, generalComments } = useMemo(() => {
    const fieldComments = {};
    const general = [];
    if (!showRequestedChanges || reviewComments.length === 0) {
      return { fieldComments, generalComments: general };
    }

    reviewComments.forEach((item, index) => {
      if (!item || typeof item !== "object") return;
      const comment = String(item.comment || "").trim();
      if (!comment) return;
      const example = String(item.example || "").trim();

      let mappedFields = [];
      const explicitField = normalizeReviewField(item.field);
      if (explicitField && Object.prototype.hasOwnProperty.call(INITIAL_FORM, explicitField)) {
        mappedFields = [explicitField];
      } else {
        const sectionKey = String(item.section || "").trim().toLowerCase();
        mappedFields = REVIEW_SECTION_FIELD_MAP[sectionKey] || [];
      }

      const normalizedComment = { comment, example, id: `review-comment-${index}` };
      if (!mappedFields.length) {
        general.push(normalizedComment);
        return;
      }

      mappedFields.forEach((fieldName) => {
        if (!Object.prototype.hasOwnProperty.call(INITIAL_FORM, fieldName)) return;
        if (!fieldComments[fieldName]) fieldComments[fieldName] = [];
        fieldComments[fieldName].push(normalizedComment);
      });
    });

    return { fieldComments, generalComments: general };
  }, [showRequestedChanges, reviewComments]);

  const getValidationErrorForField = (fieldName) => {
    if (fieldName === "email_domains_text") {
      return errors.email_domains || errors.email_domains_text || "";
    }
    return errors[fieldName] || "";
  };

  const renderFieldFeedback = (fieldName) => {
    const comments = reviewCommentsByField[fieldName] || [];
    const validationError = getValidationErrorForField(fieldName);
    if (!comments.length && !validationError) return null;

    return (
      <div className="space-y-1">
        {comments.map((item, index) => (
          <div
            key={`${fieldName}-${item.id}-${index}`}
            className="rounded border border-amber-200 bg-amber-50 px-2.5 py-2"
          >
            <p className="text-xs text-amber-800">{item.comment}</p>
            {item.example && <p className="text-[11px] text-amber-700 mt-1">Example: {item.example}</p>}
          </div>
        ))}
        {validationError && (
          <p className="text-xs text-red-600">{validationError}</p>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen w-screen bg-slate-50 font-sans text-slate-700">
      <aside className="hidden md:flex fixed left-0 top-0 h-screen w-72 flex-col text-white" style={{ backgroundColor: "#003D4C" }}>
        <div className="px-6 py-4 border-b h-20 flex items-center" style={{ borderColor: "#E5E7EB" }}>
          <div className="flex items-center gap-3 pr-8">
            <LogoOneDoor className="h-[48px] w-[48px]" imgClassName="h-[42px] w-auto object-contain" />
            <div>
              <h1 className="text-lg font-bold">OneDoor</h1>
              <p className="text-xs" style={{ color: "#B8D4DB" }}>Merch Cloud Portal</p>
            </div>
          </div>
        </div>
        <div className="flex-1 px-4 py-6 overflow-y-auto" />
        <div className="p-4 border-t" style={{ borderColor: "#E5E7EB" }}>
          <div className="text-xs" style={{ color: "#B8D4DB" }}>Onboarding mode</div>
        </div>
      </aside>

      <div className="min-h-screen flex flex-col transition-all duration-300 md:ml-72">
        <header className="h-20 bg-white border-b border-slate-200 px-4 md:px-8 flex items-center">
          <div className="flex items-center gap-3">
            <img src={logoSrc} alt={logoAlt} className="h-10 w-auto max-w-[140px] object-contain object-left" draggable={false} />
            <h2 className="text-lg md:text-2xl font-bold text-slate-800" id="dashboard-title">
              Onboarding: IDP Provisioning
            </h2>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6" role="main" aria-labelledby="dashboard-title">
          <div className="max-w-2xl">
            {loading && (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-700 mx-auto" />
                <p className="mt-4 text-slate-600">Loading onboarding details...</p>
              </div>
            )}

            {!loading && status.type === "error" && (
              <div className="mx-auto max-w-3xl rounded-lg border border-slate-200 bg-white p-6 md:p-8 shadow-sm">
                <h3 className="text-2xl font-bold text-slate-800 mb-3">{status.title}</h3>
                <p className="text-slate-600">{status.message}</p>
              </div>
            )}

            {!loading && status.type === "success" && (
              <div className="mx-auto max-w-3xl rounded-lg border border-slate-200 bg-white p-6 md:p-8 shadow-sm">
                <h3 className="text-2xl font-bold text-slate-800 mb-3">{status.title}</h3>
                <p className="text-slate-600 mb-4">{status.message}</p>
                <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                  You can close this tab now.
                </div>
              </div>
            )}

            {!loading && !status.type && customer && requiresLogin && (
              <div className="mx-auto max-w-3xl rounded-lg border border-slate-200 bg-white p-6 md:p-8 shadow-sm space-y-4">
                <h3 className="text-2xl font-bold text-slate-800">Log in to continue onboarding</h3>
                <p className="text-slate-600">
                  Use the email and temporary password from your onboarding invite.
                </p>
                <form onSubmit={handleLoginSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="login_email" className="block text-sm font-semibold text-slate-700">Email</label>
                    <input
                      id="login_email"
                      name="login_email"
                      type="email"
                      value={loginForm.email}
                      onChange={(event) => {
                        const value = event.target.value;
                        setLoginForm((prev) => ({ ...prev, email: value }));
                      }}
                      className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-slate-500 focus:outline-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="login_password" className="block text-sm font-semibold text-slate-700">Temporary password</label>
                    <input
                      id="login_password"
                      name="login_password"
                      type="password"
                      value={loginForm.password}
                      onChange={(event) => {
                        const value = event.target.value;
                        setLoginForm((prev) => ({ ...prev, password: value }));
                      }}
                      className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-slate-500 focus:outline-none"
                    />
                  </div>
                  {loginError && (
                    <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                      {loginError}
                    </div>
                  )}
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={authenticating || !isLoginDirty}
                      className={`inline-flex items-center justify-center rounded px-5 py-2.5 text-sm font-semibold text-white focus:outline-none ${
                        authenticating || !isLoginDirty ? "bg-slate-400" : "bg-[#003D4C] hover:bg-[#004D5C]"
                      }`}
                    >
                      {authenticating ? "Logging in..." : "Log in"}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {!loading && !status.type && customer && !requiresLogin && (
              <>
                <div className="mb-4 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                  <h3 className="text-2xl font-bold text-slate-800 mb-2">
                    Identity setup for {customer.name || customer.slug}
                  </h3>
                  {customer.onboarding_review_status === "changes_requested" ? (
                    <p className="text-amber-700">
                      Your previous submission was reviewed and updates were requested. Please update the sections below
                      and submit again.
                    </p>
                  ) : (
                    <p className="text-slate-600">
                      Provide your identity provider details. One Door will configure Cognito brokering after review.
                    </p>
                  )}
                  {customer.onboarding_review_status === "changes_requested" && customer.onboarding_reviewed_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      Reviewed {new Date(customer.onboarding_reviewed_at).toLocaleString()}
                      {customer.onboarding_reviewed_by ? ` by ${customer.onboarding_reviewed_by}` : ""}.
                    </p>
                  )}
                </div>

                {showRequestedChanges && reviewComments.length > 0 && (
                  <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-5 shadow-sm space-y-2">
                    <h4 className="text-base font-semibold text-amber-800">Updates requested</h4>
                    <p className="text-sm text-amber-700">
                      Comments are shown under each field that needs to be corrected.
                    </p>
                    {generalComments.length > 0 && (
                      <div className="space-y-2">
                        {generalComments.map((item, index) => (
                          <div key={`general-review-comment-${index}`} className="rounded border border-amber-200 bg-white px-3 py-2">
                            <p className="text-xs text-amber-800">{item.comment}</p>
                            {item.example && <p className="text-[11px] text-amber-700 mt-1">Example: {item.example}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="w-full rounded-lg border border-slate-200 bg-white p-6 md:p-8 shadow-sm space-y-5">
                  <div className="space-y-2">
                    <label htmlFor="broker_provider" className="block text-sm font-semibold text-slate-700">Identity type</label>
                    <Select
                      id="broker_provider"
                      value={form.broker_provider}
                      onChange={(value) => {
                        const nextBrokerProvider = String(value || "");
                        setForm((prev) => ({
                          ...prev,
                          broker_provider: nextBrokerProvider,
                          ...(nextBrokerProvider === "cognito" ? { local_cognito_enabled: "true" } : {}),
                        }));
                        clearFieldErrors("broker_provider");
                        if (nextBrokerProvider === "cognito") clearFieldErrors("local_cognito_enabled");
                      }}
                      options={BROKER_PROVIDER_OPTIONS}
                      className="min-h-[40px]"
                    />
                    {oidcHint && (
                      <p className="text-xs text-slate-500">{oidcHint}</p>
                    )}
                    {renderFieldFeedback("broker_provider")}
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="environment" className="block text-sm font-semibold text-slate-700">Environment</label>
                    <Select
                      id="environment"
                      value={form.environment}
                      onChange={(value) => {
                        setForm((prev) => ({ ...prev, environment: String(value) }));
                        clearFieldErrors("environment");
                      }}
                      options={environmentOptions}
                      className="min-h-[40px]"
                    />
                    {renderFieldFeedback("environment")}
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="idp_display_name" className="block text-sm font-semibold text-slate-700">
                      Display name (e.g. Sign in with Acme SSO)
                    </label>
                    <input
                      id="idp_display_name"
                      name="idp_display_name"
                      type="text"
                      value={form.idp_display_name}
                      onChange={handleInputChange}
                      className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-slate-500 focus:outline-none"
                    />
                    {renderFieldFeedback("idp_display_name")}
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="email_domains_text" className="block text-sm font-semibold text-slate-700">
                      Email domains (one per line or comma-separated, e.g. @acme.com)
                    </label>
                    <textarea
                      id="email_domains_text"
                      name="email_domains_text"
                      rows={2}
                      value={form.email_domains_text}
                      onChange={handleInputChange}
                      className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-slate-500 focus:outline-none"
                    />
                    {renderFieldFeedback("email_domains_text")}
                  </div>

                  <fieldset className="space-y-2">
                    <legend className="block text-sm font-semibold text-slate-700">
                      Allow local Cognito users?
                    </legend>
                    <p className="text-xs text-slate-500">
                      {isManagedCognitoProvider
                        ? "Automatically enabled for Cognito. Choose another identity type to edit this."
                        : "Choose whether users with local Cognito accounts can sign in without a matching email-domain mapping."}
                    </p>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="local_cognito_enabled_true" className="inline-flex items-center gap-2 text-sm text-slate-700">
                        <input
                          id="local_cognito_enabled_true"
                          type="radio"
                          name="local_cognito_enabled"
                          value="true"
                          checked={isManagedCognitoProvider || form.local_cognito_enabled === "true"}
                          disabled={isManagedCognitoProvider}
                          onChange={(event) => {
                            setForm((prev) => ({ ...prev, local_cognito_enabled: event.target.value }));
                            clearFieldErrors("local_cognito_enabled");
                          }}
                          className="h-4 w-4 border-slate-300 text-slate-700 focus:ring-slate-500"
                        />
                        Yes, allow local Cognito users
                      </label>
                      <label htmlFor="local_cognito_enabled_false" className="inline-flex items-center gap-2 text-sm text-slate-700">
                        <input
                          id="local_cognito_enabled_false"
                          type="radio"
                          name="local_cognito_enabled"
                          value="false"
                          checked={!isManagedCognitoProvider && form.local_cognito_enabled === "false"}
                          disabled={isManagedCognitoProvider}
                          onChange={(event) => {
                            setForm((prev) => ({ ...prev, local_cognito_enabled: event.target.value }));
                            clearFieldErrors("local_cognito_enabled");
                          }}
                          className="h-4 w-4 border-slate-300 text-slate-700 focus:ring-slate-500"
                        />
                        No, require mapped identity-provider domains only
                      </label>
                    </div>
                    {renderFieldFeedback("local_cognito_enabled")}
                  </fieldset>

                  {isSamlProvider && (
                    <div className="space-y-4 rounded border border-slate-200 bg-slate-50 p-4">
                      <h4 className="text-sm font-semibold text-slate-700">SAML details</h4>
                      <div className="space-y-2">
                        <label htmlFor="metadata_url" className="block text-sm font-semibold text-slate-700">Metadata URL</label>
                        <input
                          id="metadata_url"
                          name="metadata_url"
                          type="url"
                          value={form.metadata_url}
                          onChange={handleInputChange}
                          className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-slate-500 focus:outline-none"
                        />
                        {renderFieldFeedback("metadata_url")}
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="metadata_xml" className="block text-sm font-semibold text-slate-700">Or paste metadata XML</label>
                        <textarea
                          id="metadata_xml"
                          name="metadata_xml"
                          rows={4}
                          value={form.metadata_xml}
                          onChange={handleInputChange}
                          className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-slate-500 focus:outline-none"
                        />
                        {renderFieldFeedback("metadata_xml")}
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="entity_id" className="block text-sm font-semibold text-slate-700">Entity ID (optional)</label>
                        <input
                          id="entity_id"
                          name="entity_id"
                          type="text"
                          value={form.entity_id}
                          onChange={handleInputChange}
                          className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-slate-500 focus:outline-none"
                        />
                        {renderFieldFeedback("entity_id")}
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="sso_url" className="block text-sm font-semibold text-slate-700">SSO URL (optional)</label>
                        <input
                          id="sso_url"
                          name="sso_url"
                          type="url"
                          value={form.sso_url}
                          onChange={handleInputChange}
                          className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-slate-500 focus:outline-none"
                        />
                        {renderFieldFeedback("sso_url")}
                      </div>
                    </div>
                  )}

                  {isOidcProvider && (
                    <div className="space-y-4 rounded border border-slate-200 bg-slate-50 p-4">
                      <h4 className="text-sm font-semibold text-slate-700">OIDC details</h4>
                      <div className="space-y-2">
                        <label htmlFor="issuer" className="block text-sm font-semibold text-slate-700">Issuer URL *</label>
                        <input
                          id="issuer"
                          name="issuer"
                          type="url"
                          placeholder="https://example.com/oauth2/default"
                          value={form.issuer}
                          onChange={handleInputChange}
                          className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-slate-500 focus:outline-none"
                        />
                        {renderFieldFeedback("issuer")}
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="client_id" className="block text-sm font-semibold text-slate-700">Client ID *</label>
                        <input
                          id="client_id"
                          name="client_id"
                          type="text"
                          value={form.client_id}
                          onChange={handleInputChange}
                          className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-slate-500 focus:outline-none"
                        />
                        {renderFieldFeedback("client_id")}
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="client_secret" className="block text-sm font-semibold text-slate-700">Client secret *</label>
                        <input
                          id="client_secret"
                          name="client_secret"
                          type="password"
                          value={form.client_secret}
                          onChange={handleInputChange}
                          className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-slate-500 focus:outline-none"
                        />
                        {renderFieldFeedback("client_secret")}
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="authorize_scopes" className="block text-sm font-semibold text-slate-700">Scopes</label>
                        <input
                          id="authorize_scopes"
                          name="authorize_scopes"
                          type="text"
                          placeholder="openid email profile"
                          value={form.authorize_scopes}
                          onChange={handleInputChange}
                          className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-slate-500 focus:outline-none"
                        />
                        {renderFieldFeedback("authorize_scopes")}
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="authorization_endpoint" className="block text-sm font-semibold text-slate-700">Authorization endpoint (optional)</label>
                        <input
                          id="authorization_endpoint"
                          name="authorization_endpoint"
                          type="url"
                          value={form.authorization_endpoint}
                          onChange={handleInputChange}
                          className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-slate-500 focus:outline-none"
                        />
                        {renderFieldFeedback("authorization_endpoint")}
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="token_endpoint" className="block text-sm font-semibold text-slate-700">Token endpoint (optional)</label>
                        <input
                          id="token_endpoint"
                          name="token_endpoint"
                          type="url"
                          value={form.token_endpoint}
                          onChange={handleInputChange}
                          className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-slate-500 focus:outline-none"
                        />
                        {renderFieldFeedback("token_endpoint")}
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="jwks_uri" className="block text-sm font-semibold text-slate-700">JWKS URI (optional)</label>
                        <input
                          id="jwks_uri"
                          name="jwks_uri"
                          type="url"
                          value={form.jwks_uri}
                          onChange={handleInputChange}
                          className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-slate-500 focus:outline-none"
                        />
                        {renderFieldFeedback("jwks_uri")}
                      </div>
                    </div>
                  )}

                  {Object.keys(errors).length > 0 && (
                    <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                      {Object.entries(errors).map(([field, message]) => `${field}: ${message}`).join("; ")}
                    </div>
                  )}

                  <div className="mt-2 flex justify-end">
                    <button
                      type="submit"
                      disabled={submitting || !isProvisioningFormDirty}
                      className={`inline-flex items-center justify-center rounded px-5 py-2.5 text-sm font-semibold text-white focus:outline-none ${
                        submitting || !isProvisioningFormDirty ? "bg-slate-400" : "bg-[#003D4C] hover:bg-[#004D5C]"
                      }`}
                    >
                      {submitting ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
}
